import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, GraduationCap, List, Columns, Copy, CalendarIcon } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useClassTrials, type ClassTrial, type ClassTrialFormData } from '@/hooks/useClassTrials';
import { CLASS_TRIAL_STATUS_OPTIONS } from '@/components/admin/class-trials/classTrialStatusColors';
import { ClassTrialTable } from '@/components/admin/class-trials/ClassTrialTable';
import { ClassTrialKanban } from '@/components/admin/class-trials/ClassTrialKanban';
import { ClassTrialFilters, type ClassTrialFiltersState } from '@/components/admin/class-trials/ClassTrialFilters';
import { ConsultantTeachersDialog } from '@/components/admin/class-trials/ConsultantTeachersDialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

function formatDateToString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDateParam(value: string | null, fallback: Date): Date {
  if (!value) return fallback;
  const d = new Date(value + 'T00:00:00');
  return isNaN(d.getTime()) ? fallback : d;
}

function parseArrayParam(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').filter(Boolean);
}

export default function AdminClassTrialsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1);

  // Read initial state from query params
  const [startDate, setStartDate] = useState<Date>(() => parseDateParam(searchParams.get('from'), defaultStart));
  const [endDate, setEndDate] = useState<Date>(() => parseDateParam(searchParams.get('to'), now));
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [consultantDialogOpen, setConsultantDialogOpen] = useState(false);
  const [filters, setFilters] = useState<ClassTrialFiltersState>(() => ({
    searchQuery: searchParams.get('q') || '',
    statusFilter: parseArrayParam(searchParams.get('status')),
    classTypeFilter: parseArrayParam(searchParams.get('type')),
    locationFilter: parseArrayParam(searchParams.get('location')),
    gradeFilter: parseArrayParam(searchParams.get('grade')),
  }));
  const [activeTab, setActiveTab] = useState(() => searchParams.get('view') || 'kanban');

  // Sync state to URL query params
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('from', formatDateToString(startDate));
    params.set('to', formatDateToString(endDate));
    if (filters.searchQuery) params.set('q', filters.searchQuery);
    if (filters.statusFilter.length) params.set('status', filters.statusFilter.join(','));
    if (filters.classTypeFilter.length) params.set('type', filters.classTypeFilter.join(','));
    if (filters.locationFilter.length) params.set('location', filters.locationFilter.join(','));
    if (filters.gradeFilter.length) params.set('grade', filters.gradeFilter.join(','));
    if (activeTab !== 'kanban') params.set('view', activeTab);
    setSearchParams(params, { replace: true });
  }, [startDate, endDate, filters, activeTab, setSearchParams]);

  const { trials, isLoading, fetchTrials, deleteTrial, updateTrialKanbanStatus } = useClassTrials();

  useEffect(() => {
    fetchTrials(formatDateToString(startDate), formatDateToString(endDate));
  }, [startDate, endDate, fetchTrials]);

  const filterOptions = useMemo(() => {
    return {
      statusOptions: [...CLASS_TRIAL_STATUS_OPTIONS],
      classTypeOptions: ["Premiere/TH", "Premiere/Inter", "Private class/TH", "Private class/Inter"],
      locationOptions: ["ออนไลน์", "งามวงศ์วาน", "บรรทัดทอง"],
      gradeOptions: ["ป.1", "ป.2", "ป.3", "ป.4", "ป.5", "ป.6", "ม.1", "ม.2", "ม.3", "ม.4", "ม.5", "ม.6", "มหาวิทยาลัย"],
    };
  }, []);

  const filteredTrials = useMemo(() => {
    return trials.filter((trial) => {
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch =
          (trial.student_name?.toLowerCase().includes(query)) ||
          (trial.student_nickname?.toLowerCase().includes(query)) ||
          (trial.teacher_code?.toLowerCase().includes(query)) ||
          (trial.order_code?.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      if (filters.statusFilter.length > 0 && !filters.statusFilter.includes(trial.status || '')) return false;
      if (filters.classTypeFilter.length > 0 && !filters.classTypeFilter.includes(trial.class_type || '')) return false;
      if (filters.locationFilter.length > 0) {
        const normalize = (v: string | null) => v === 'สยาม' ? 'บรรทัดทอง' : v;
        if (!filters.locationFilter.includes(normalize(trial.location) || '')) return false;
      }
      if (filters.gradeFilter.length > 0 && !filters.gradeFilter.includes(trial.grade || '')) return false;
      return true;
    });
  }, [trials, filters]);

  const handleEdit = (trial: ClassTrial) => {
    navigate(`/admin/class-trials/${trial.id}/edit`);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const success = await deleteTrial(deleteId);
    if (success) {
      fetchTrials(formatDateToString(startDate), formatDateToString(endDate));
    }
    setDeleteId(null);
  };

  const totalCost = filteredTrials.reduce((sum, t) => sum + (t.cost || 0), 0);
  const closedSales = filteredTrials.filter(t => t.status === 'ปิดการขายเรียบร้อย').length;

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <AdminSidebar />
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            {/* Fixed header area */}
            <div className="flex-shrink-0 p-6 pb-4 space-y-4">
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <GraduationCap className="h-8 w-8 text-primary" />
                  <div>
                    <h1 className="text-2xl font-bold">ทดลองเรียน</h1>
                    <p className="text-muted-foreground">จัดการข้อมูลการทดลองเรียน</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setConsultantDialogOpen(true)}>
                    จัดการครูที่ปรึกษา
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/trial-form`);
                      toast({ title: 'คัดลอกลิงก์แล้ว', description: 'ลิงก์ฟอร์มทดลองเรียนถูกคัดลอกไปยัง clipboard' });
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    คัดลอกลิงก์
                  </Button>
                  <Button onClick={() => navigate('/admin/class-trials/new/edit')}>
                    <Plus className="h-4 w-4 mr-2" />
                    เพิ่มทดลองเรียน
                  </Button>
                </div>
              </div>
              {/* Date Range Selector */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">ช่วงเวลา:</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, 'd MMM yyyy', { locale: th })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
                <span className="text-sm text-muted-foreground">ถึง</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(endDate, 'd MMM yyyy', { locale: th })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} initialFocus className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Filters */}
              <ClassTrialFilters
                filters={filters}
                onFiltersChange={setFilters}
                statusOptions={filterOptions.statusOptions}
                classTypeOptions={filterOptions.classTypeOptions}
                locationOptions={filterOptions.locationOptions}
                gradeOptions={filterOptions.gradeOptions}
              />

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card border rounded-lg p-4">
                  <div className="text-sm text-muted-foreground">จำนวนทดลองเรียน</div>
                  <div className="text-2xl font-bold">{filteredTrials.length}</div>
                </div>
                <div className="bg-card border rounded-lg p-4">
                  <div className="text-sm text-muted-foreground">ปิดการขายสำเร็จ</div>
                  <div className="text-2xl font-bold text-green-600">{closedSales}</div>
                </div>
                <div className="bg-card border rounded-lg p-4">
                  <div className="text-sm text-muted-foreground">ค่าใช้จ่ายรวม</div>
                  <div className="text-2xl font-bold">{totalCost.toLocaleString()} ฿</div>
                </div>
              </div>

              {/* Tab triggers */}
              <TabsList>
                <TabsTrigger value="list" className="gap-2">
                  <List className="h-4 w-4" />
                  รายการ
                </TabsTrigger>
                <TabsTrigger value="kanban" className="gap-2">
                  <Columns className="h-4 w-4" />
                  Kanban
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Scrollable content */}
            <TabsContent value="list" className="mt-0 flex-1 overflow-auto px-6 pb-6">
              <ClassTrialTable
                trials={filteredTrials}
                isLoading={isLoading}
                onEdit={handleEdit}
                onDelete={(id) => setDeleteId(id)}
              />
            </TabsContent>
            <TabsContent value="kanban" className="mt-0 flex-1 overflow-auto px-6 pb-6">
              <ClassTrialKanban
                trials={filteredTrials}
                isLoading={isLoading}
                onStatusChange={updateTrialKanbanStatus}
              />
            </TabsContent>
          </Tabs>

          {/* Delete Confirmation */}
          <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>ยืนยันการลบ</AlertDialogTitle>
                <AlertDialogDescription>
                  คุณต้องการลบข้อมูลทดลองเรียนนี้หรือไม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                  ลบ
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <ConsultantTeachersDialog open={consultantDialogOpen} onOpenChange={setConsultantDialogOpen} />
        </main>
      </div>
    </SidebarProvider>
  );
}
